import { Component, Input, OnInit } from '@angular/core';
import { Business } from '../models/business.model';
import { BusinessFuel, FuelType, ServiceType } from '../models/business-fuel.model';
import { hasTag } from '../../util/tag-utils';
import { IConditionalSection } from '../../shared/models/condition-section.model';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { Tag } from '../../shared/models/tag.model';
import { BusinessTag } from '../models/business-tag.model';
import { MatCheckboxChange, MatDialog, MatDialogConfig, MatSnackBar } from '@angular/material';
import { TagService } from '../../shared/services/tag.service';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { EditFuelComponent } from '../edit-fuel/edit-fuel.component';
import { DeleteFuelComponent } from '../delete-fuel/delete-fuel.component';


@Component({
  selector: 'app-business-fuel',
  templateUrl: './business-fuel.component.html',
  styleUrls: ['./business-fuel.component.scss']
})
export class BusinessFuelComponent implements IConditionalSection, OnInit {
  @Input() business: Business;
  hasTag = hasTag;
  editModeSet = false;
  privateTag: Tag;
  isPrivate: boolean;
  isRegionalManager = false;

  constructor(private _toggleService: EditToggleService,
              private _tagService: TagService,
              private _businessManagerService: BusinessManagerService,
              public _snackBar: MatSnackBar,
              private _editFuelDialog: MatDialog,
              private _deleteFuelDialog: MatDialog) {
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
  }

  async ngOnInit() {
    const allTags = await this._tagService.getAllTags();
    this.setPrivate(allTags);
    this.isRegionalManager = await this._businessManagerService.isRegionalManager()
      && !await this._businessManagerService.userOwnsBusiness(this.business.id);
  }

  get hasContent(): boolean {
    return this.business && this.business.fuel && this.business.fuel.length > 0;
  }

  GetFuelType(input: FuelType): string {
    return BusinessFuel.formatFuelType(input);
  }

  GetServiceType(input: ServiceType): string {
    return BusinessFuel.formatServiceType(input);
  }

  async setPrivate(allTags: BusinessTag[]) {
    this.privateTag = await this._tagService.findTagByName(allTags, 'Fuel::Private');
    this.isPrivate = hasTag(this.business.tags, 'Fuel::Private');
  }

  isChecked(tagId: number): boolean {
    const TAGS = this.business.tags;
    for (const tag of TAGS) {
      if (tag.tagId === tagId) {
        return true;
      }
    }
    return false;
  }

  async checkBoxChanged (event: MatCheckboxChange) {
    const ERROR_MSG = 'Unable to save change, please try back later, or call 1-800-USA-AOPA if the error persists.';
    const TAG_ID = +event.source.id;
    const BUSINESS_ID = this.business.id;
    try {
      if (event.source.checked) {
        await this._tagService.postTag(TAG_ID, BUSINESS_ID);
      } else {
        await this._tagService.deleteTag(TAG_ID, BUSINESS_ID);
      }
      const newBusiness = await this._businessManagerService.updateBusiness(this.business);
      Object.assign(this.business, newBusiness);
    } catch (e) {
      this._snackBar.open(ERROR_MSG, 'Error', {
        duration: 6000
      });
      throw e;
    }
  }

  openEditFuelDialog(index: number) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.data = {
      businessID: this.business.id,
      fuelID: null,
      isNew: false
    };
    index == null ? dialogConfig.data.isNew = true : dialogConfig.data.fuel = this.business.fuel[index];
    dialogConfig.data['busID'] = this.business.id;
    dialogConfig.width = '60%';
    const dialogRef = this._editFuelDialog.open(EditFuelComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        if (isUpdated.isNew) {
          this.business.fuel.push(isUpdated.contact);
          this._snackBar.open('Fuel', 'Added', {
            duration: 2000
          });
        } else {
          this._snackBar.open('Fuel', 'Updated', {
            duration: 2000
          });
        }
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  deleteFuel(index: number) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      businessID: this.business.id,
      fuelID: this.business.fuel[index].id
    };
    const dialogRef = this._deleteFuelDialog.open(DeleteFuelComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this._snackBar.open('Fuel Info', 'Deleted', {
          duration: 2000
        });
        this.business.fuel.splice(index, 1);
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }
}
